import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CookieConsent } from "@/components/CookieConsent";
import {
  CinematicHero,
  SectionHead,
  Reveal,
  FullBleedQuote,
  SplitFeature,
  NumberedSteps,
  StatStripe,
  CinematicCTA,
  CinematicFAQ,
  TripleStrip,
} from "@/components/service/Cinematic";
import heroImg from "@/assets/bathroom.jpg";
import b1 from "@/assets/bathroom-ref-1.jpg";
import b2 from "@/assets/bathroom-ref-2.jpg";
import b3 from "@/assets/bathroom-ref-3.jpg";
import b4 from "@/assets/bathroom-ref-4.jpg";
import b5 from "@/assets/bathroom-ref-5.jpg";
import b6 from "@/assets/bathroom-ref-6.jpg";
import b7 from "@/assets/bathroom-ref-7.webp";

const materialien = [
  {
    title: "Mikrozement",
    subtitle: "Fugenlos, mineralisch, individuell",
    text: "Zementbasiertes Beschichtungssystem für Wände, Böden, Duschen und Möbel – nur 2–3 mm Schichtstärke. Mit Polyurethan versiegelt: wasserdicht, kratzfest, pflegeleicht. Über 50 Farbtöne, matt bis glänzend.",
  },
  {
    title: "Kunstharz / Epoxid",
    subtitle: "Robust, gleichmäßig, fugenlos",
    text: "Noch robuster als Mikrozement – ideal für stark beanspruchte Flächen oder ein besonders gleichmäßiges, glänzendes Finish. Ebenfalls fugenlos und hygienisch.",
  },
];

const steps = [
  { title: "Beratung & Aufmaß vor Ort", desc: "Wir besuchen Sie in Herford, schauen das Bad an, besprechen Wünsche, Materialien und Budget – kostenlos und unverbindlich." },
  { title: "Konzept & Festpreis", desc: "Detailliertes Angebot mit Materialmustern, Farbtönen und verbindlichem Festpreis." },
  { title: "Schutzmaßnahmen", desc: "Möbel, Böden und angrenzende Räume werden geschützt. Sauberkeit ist Ehrensache." },
  { title: "Abriss & Abdichtung", desc: "Alte Fliesen raus, Untergrund prüfen, fachgerecht abdichten und für die fugenlose Beschichtung vorbereiten." },
  { title: "Beschichtung oder Fliese", desc: "Ob fugenloses Mikrozement, Harzbeschichtung oder klassische Fliese – Schicht für Schicht in Meisterqualität." },
  { title: "Versiegelung & Übergabe", desc: "Finale Polyurethan-Versiegelung, Endreinigung, gemeinsame Übergabe Ihres neuen Bades." },
];

const faqs = [
  { q: "Was kostet eine fugenlose Badsanierung in Herford?", a: "Mikrozement auf Wand + Boden (ca. 15–20 m²) kostet je nach Untergrundzustand und System 3.500–8.000 €. Eine Komplettsanierung inkl. Abriss und Neuaufbau liegt bei 8.000–20.000 € – abhängig von Größe, Material und sanitären Arbeiten." },
  { q: "Wie lange dauert eine Badrenovierung?", a: "Eine reine Fugenlos-Beschichtung (ohne Abriss) dauert 5–8 Werktage. Komplettsanierung mit Abriss, Abdichtung und Neuaufbau 2–4 Wochen. Wir takten den Ablauf so, dass Sie schnellstmöglich wieder nutzen können." },
  { q: "Ist Mikrozement wirklich wasserdicht?", a: "Nach Versiegelung mit mehreren Schichten Polyurethan vollständig wasserdicht. Wird regulär in Duschen, Badewannen und auf Fußböden eingesetzt. Entscheidend: fachgerechte Verarbeitung – das garantieren wir als Meisterbetrieb." },
  { q: "Bleiben fugenlose Bäder dauerhaft schön?", a: "Bei korrekter Versiegelung 10+ Jahre wartungsarm. Keine vergilbenden Silikonfugen, keine schwarzen Schimmelränder. Eine gelegentliche Nachpflege mit dem Versiegelungs-Refresher hält die Oberfläche dauerhaft hochwertig." },
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Badrenovierung & Fugenlose Bäder",
  name: "Badrenovierung & Fugenlose Bäder Herford",
  provider: { "@type": "LocalBusiness", name: "Malerfachbetrieb Stein GmbH & Co. KG" },
  areaServed: "Herford",
  description: "Badrenovierung in Herford – fugenlose Bäder, Mikrozement und Komplettsanierung aus Meisterhand.",
};

const BadrenovierungHerford = () => (
  <div className="min-h-screen bg-background">
    <SEO
      title="Badrenovierung in Herford: Fugenlose Bäder & Mikrozement"
      description="Badrenovierung in Herford: Fugenlose Wände & Böden in Mikrozement und Harz – hygienisch, pflegeleicht, zeitlos. Bad von A–Z aus einer Hand. ☎ 05221 3832130"
      keywords="Badrenovierung Herford, Badsanierung Herford, fugenloses Bad Herford, Mikrozement Bad Herford, Harzboden Bad, Fliesen ohne Fugen, Bad modernisieren Herford"
      canonical="https://malerfachbetrieb-stein.de/badrenovierung-fugenlose-baeder-herford"
      structuredData={structuredData}
    />
    <Navigation />
    <WhatsAppButton />

    <main>
      <CinematicHero
        chapter="Kapitel 04 / Bad"
        eyebrow="Badrenovierung & Fugenlose Bäder"
        title={<>Schluss mit vergilbten <em className="not-italic text-background/70">Fugen</em>.</>}
        subtitle="Von der Neugestaltung bis zur Komplettsanierung. Unsere Spezialität: fugenlose Wände und Böden in Mikrozement und Harz – hygienisch, pflegeleicht, zeitlos."
        image={heroImg}
        imageAlt="Badrenovierung Herford – fugenloses Bad in Mikrozement"
        ctaLabel="Badberatung anfragen"
      />

      <StatStripe
        stats={[
          { value: "0", label: "Schimmelfugen" },
          { value: "50+", label: "Mikrozement-Farbtöne" },
          { value: "10+", label: "Jahre haltbar" },
          { value: "2–4", label: "Wochen Bauzeit" },
        ]}
      />

      <FullBleedQuote
        image={b6}
        imageAlt="Designbad mit fugenlosem Mikrozement"
        quote={<>„Ein Bad sollte morgens gut tun – und sich abends pflegen lassen, ohne dass es Arbeit ist.“</>}
        caption="Konzept fugenlos"
      />

      {/* Leistungen split */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Leistungen"
            title={<>Drei Wege zum<br /> neuen Bad.</>}
          />
          <div className="space-y-24 md:space-y-32">
            <SplitFeature
              index="01"
              eyebrow="Fugenlos"
              title="Mikrozement & Harz – die hygienische Antwort."
              body="Keine Fugen, kein Schimmel, keine gelben Verfärbungen. Wir realisieren fugenlose Bäder mit Mikrozement, Harzversiegelung und fugenlosen Beschichtungs­systemen auf Wand und Boden."
              bullets={["Mikrozement 2–3 mm Aufbau", "Polyurethan-Versiegelung", "Über 50 Farbtöne", "Hygienisch & pflegeleicht"]}
              image={b1}
              imageAlt="Fugenloses Bad in Mikrozement"
            />
            <SplitFeature
              reverse
              index="02"
              eyebrow="Klassisch"
              title="Fliesenarbeiten in handwerklicher Präzision."
              body="Großformatig, kleinteilig, diagonal, im Verband. Fachgerechte Abdichtung, saubere Untergrundvorbereitung und exakte Verfugung – wenn Sie Fliesen lieben, fliesen wir kompromisslos sauber."
              image={b2}
              imageAlt="Walk-In-Dusche mit Fliesen"
            />
            <SplitFeature
              index="03"
              eyebrow="Komplett"
              title="Bad von A bis Z – ein Ansprechpartner."
              body="Abriss, Abdichtung, Estrich, Fliesen oder fugenlos, Malerarbeiten, sanitäre Anschlüsse über Partnerbetrieb. Sie haben einen Ansprechpartner – wir koordinieren den Rest."
              image={b3}
              imageAlt="Komplett saniertes Designbad"
            />
          </div>
        </div>
      </section>

      {/* Materialien */}
      <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
        <div className="container mx-auto">
          <SectionHead
            invert
            eyebrow="Materialien"
            title="Mikrozement & Harz im Detail."
          />
          <div className="grid md:grid-cols-2 gap-px bg-background/10">
            {materialien.map((m, i) => (
              <Reveal key={m.title} delay={i * 100}>
                <div className="bg-foreground p-8 md:p-12 h-full">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-background/50 mb-3">
                    {String(i + 1).padStart(2, "0")} / {m.subtitle}
                  </div>
                  <h3 className="font-caslon text-3xl md:text-4xl font-bold mb-5">{m.title}</h3>
                  <p className="text-background/70 leading-relaxed">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Ablauf"
            title="So entsteht Ihr neues Bad."
          />
          <NumberedSteps steps={steps} />
        </div>
      </section>

      <TripleStrip
        images={[
          { src: b4, alt: "Bad Referenz", caption: "Walk-In Dusche fugenlos" },
          { src: b5, alt: "Bad Referenz", caption: "Mikrozement Wand & Boden" },
          { src: b7, alt: "Bad Referenz", caption: "Designbad Herford" },
        ]}
      />

      <CinematicFAQ
        faqs={faqs}
        title="Fragen zur Badrenovierung."
      />

      <CinematicCTA
        image={b6}
        imageAlt="Fugenloses Designbad"
        title={<>Ihr Traumbad<br /> wartet.</>}
        body="Kostenlose Beratung mit Materialmustern – direkt bei Ihnen oder in unserem Showroom."
        ctaLabel="Badberatung anfragen"
      />
    </main>

    <Footer />
    <CookieConsent />
  </div>
);

export default BadrenovierungHerford;